import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import NivoChartRenderer from './NivoChartRenderer.jsx';
import ChartInfoDrawer from './ChartInfoDrawer.jsx';
import { transformChartData, formatStepLabel } from '../../utils/chartHelpers.js';
import './ChartDisplay.css';

/**
 * Main component for displaying charts based on API data
 * @param {Object} props - Component props
 * @param {Array} props.messages - Array of chat messages
 * @param {string} props.selectedMessageId - ID of selected message (if any)
 * @param {boolean} props.hideHeader - Whether to hide the chart header (for modal mode)
 * @param {string} props.externalChartType - Chart type controlled externally (for modal mode)
 * @param {Function} props.onChartTypeChange - Callback when chart type changes externally
 * @returns {JSX.Element} Chart display component
 */
const ChartDisplay = ({ messages, selectedMessageId, hideHeader = false, externalChartType, onChartTypeChange, externalStepIndex }) => {
  const { t } = useTranslation();
  const [currentChartData, setCurrentChartData] = useState(null);
  const [selectedChartType, setSelectedChartType] = useState(null);
  const [currentQueryId, setCurrentQueryId] = useState(null);
  const [selectedStepIndex, setSelectedStepIndex] = useState(0);
  const [rawDataArray, setRawDataArray] = useState(null);

  // Reset step index whenever the selected message or message list changes
  useEffect(() => {
    setSelectedStepIndex(0);
  }, [selectedMessageId, messages.length]);

  // Sync externally controlled step index (e.g. from GraphModal)
  useEffect(() => {
    if (externalStepIndex !== undefined && externalStepIndex !== null) {
      setSelectedStepIndex(externalStepIndex);
    }
  }, [externalStepIndex]);

  // Find the message and transform the data for the current step
  useEffect(() => {
    let messageToDisplay;

    if (selectedMessageId) {
      messageToDisplay = messages.find(msg => msg.id === selectedMessageId);
    }

    if (!messageToDisplay) {
      // Fall back to latest assistant message that has at least one non-null step
      messageToDisplay = messages
        .filter(msg =>
          msg.role === 'assistant' &&
          Array.isArray(msg.metadata?.data) &&
          msg.metadata.data.some(d => d != null)
        )
        .pop();
    }

    const dataArr = messageToDisplay?.metadata?.data;

    if (Array.isArray(dataArr) && dataArr.some(d => d != null)) {
      setRawDataArray(dataArr);
      const stepData = dataArr[selectedStepIndex] || dataArr.find(d => d != null);
      const transformedData = transformChartData(stepData);

      if (transformedData) {
        setCurrentChartData(transformedData);
        if (externalChartType) {
          setSelectedChartType(externalChartType);
        } else {
          setSelectedChartType(transformedData.chartConfig?.chart_type || 'table');
        }
        const rawQueryId = messageToDisplay.metadata.queryId;
        setCurrentQueryId(
          Array.isArray(rawQueryId) ? (rawQueryId[selectedStepIndex] || null) : (rawQueryId || null)
        );
      }
    } else {
      setCurrentChartData(null);
      setSelectedChartType(null);
      setCurrentQueryId(null);
      setRawDataArray(null);
    }
  }, [messages, selectedMessageId, externalChartType, selectedStepIndex]);

  // Sync external chart type changes
  useEffect(() => {
    if (externalChartType && externalChartType !== selectedChartType) {
      setSelectedChartType(externalChartType);
    }
  }, [externalChartType]);

  if (!currentChartData) {
    return (
      <div className="chart-display">
        <div className="no-data-message">
          <h3>{t('charts.visualizations')}</h3>
          <p>{t('charts.noDataMessage')}</p>
        </div>
      </div>
    );
  }

  const { data, columns, dataType, chartTypes, seriesInfo, metadata, columnDisplayNames, columnValueTypes, chartConfig } = currentChartData;

  // Number of steps that actually have data (used to decide whether to show the step selector)
  const nonNullSteps = rawDataArray ? rawDataArray.map((d, i) => ({ d, i })).filter(s => s.d != null) : [];

  return (
    <div className="chart-display">
      <div className="chart-content">
        <NivoChartRenderer
          data={data}
          chartType={selectedChartType}
          columns={columns}
          dataType={dataType}
          chartTypes={chartTypes}
          seriesInfo={seriesInfo}
          metadata={metadata}
          columnDisplayNames={columnDisplayNames}
          columnValueTypes={columnValueTypes}
          chartConfig={chartConfig}
          onChartTypeChange={(newType) => {
            setSelectedChartType(newType);
            if (onChartTypeChange) {
              onChartTypeChange(newType);
            }
          }}
          queryId={currentQueryId}
          disabled={!currentChartData}
          hideHeader={hideHeader}
          stepSelector={!hideHeader && nonNullSteps.length > 1 ? (
            <div className="inline-chart-type-selector">
              <select
                className="chart-type-select"
                value={selectedStepIndex}
                onChange={(e) => setSelectedStepIndex(Number(e.target.value))}
              >
                {nonNullSteps.map(({ d, i }, tabIdx) => (
                  <option key={i} value={i}>
                    {formatStepLabel(d?.metadata?.data_sources, tabIdx, t)}
                  </option>
                ))}
              </select>
            </div>
          ) : null}
        />
      </div>

      <ChartInfoDrawer 
        metadata={metadata}
        dataType={dataType}
      />
    </div>
  );
};

export default ChartDisplay;
